import { DownOutlined, UpOutlined } from '@ant-design/icons';
import { Brand, Product, ProductFamily, WaterbodyDetail } from '@omniafishing/core';
import { Button } from 'antd';
import classNames from 'classnames';
import React from 'react';
import AnimateHeight from 'react-animate-height';
import { Link } from 'react-router-dom';
import { getImgixPath } from '../../lib/imgix';
import { setShopifyImgWidth } from '../../lib/set_shopify_img_width';
import { toDollars } from '../../lib/to_dollars';
import { WebAnalytics } from '../../lib/web_analytics';
import { RoutePaths } from '../../routes';
import { PRODUCTS_NAV_HEIGHT_APPROX } from '../products_nav/products_nav';
import { ProductDetailGrid } from '../product_detail_grid/product_detail_grid';
import styles from './product_family_rows.less';

export interface ProductFamilyRowsProps {
  brand: Brand;
  productFamily: ProductFamily;
  products: Product[];
  waterbody?: WaterbodyDetail;
  className?: string;
  openAndScrollToFishingReportsPanel?: () => void;
}

const initialState = {
  expanded: false,
};
type State = Readonly<typeof initialState>;

export default class ProductFamilyRows extends React.PureComponent<ProductFamilyRowsProps, State> {
  state = initialState;
  divRef = React.createRef<HTMLDivElement>();

  open = () => () => {
    this.setState({ expanded: true });
  };

  close = () => {
    window.scrollTo({
      top:
        this.divRef.current.getBoundingClientRect().top +
        window.scrollY -
        PRODUCTS_NAV_HEIGHT_APPROX,
      behavior: 'smooth',
    });
    this.setState({ expanded: false });
  };

  get ExpandButton() {
    let text = 'Quick View';
    let onClick = this.open();
    let icon = <DownOutlined />;
    if (this.state.expanded) {
      text = 'Hide Quick View';
      onClick = this.close;
      icon = <UpOutlined />;
    }
    return (
      <button className={styles.expandButton} onClick={onClick}>
        {icon} {text}
      </button>
    );
  }

  get ListButton() {
    let text = 'Quick View';
    let onClick = this.open();
    if (this.state.expanded) {
      text = 'Hide Quick View';
      onClick = this.close;
    }
    return (
      <Button block onClick={onClick} className={styles.listButton}>
        {text}
      </Button>
    );
  }

  get newColors() {
    const { products } = this.props;
    const newColorProducts = products.filter((product) => product.tags.indexOf('new_colors') > -1);
    const newColors = newColorProducts.map((product) => {
      const color = product.shopify_options.filter(
        (option) => option.name.toLowerCase() === 'color'
      )[0];
      if (color) {
        return color.value;
      }
      return null;
    });
    return newColors.filter(Boolean);
  }

  get ColorOption() {
    const { productFamily } = this.props;
    const colorOption = productFamily.options.filter(
      (option) => option.name.toLowerCase() === 'color'
    )[0];
    if (!colorOption || colorOption.values.length <= 1) {
      return null;
    }
    const newColors = this.newColors;
    return (
      <div>
        <p className={styles.optionTitle}>Colors:</p>
        <div key={colorOption.name} className={styles.colors}>
          {colorOption.values.map((value) => {
            const recommendedClass = value.recommended ? styles.recommended : null;
            const isNewColor = newColors.indexOf(value.value) > -1;
            return (
              <div className={styles.color} key={value.value} onClick={this.open()}>
                <img
                  src={setShopifyImgWidth(value.src, 300)}
                  alt={value.value}
                  width="100"
                  className={recommendedClass}
                />
                {isNewColor && <span className={styles.newColorTag}>NEW</span>}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  get NonColorOptions() {
    const nonColorOptions = this.props.productFamily.options.filter(
      (option) => option.name.toLowerCase() !== 'color'
    );
    return nonColorOptions.map((option) => (
      <div key={option.name}>
        <p className={styles.optionTitle}>{option.name}:</p>
        {option.values.map((value) => {
          const recommendedClass = value.recommended ? styles.recommended : null;
          return (
            <span
              className={classNames(styles.optionText, recommendedClass)}
              key={value.value}
              onClick={this.open()}
            >
              {value.value}
            </span>
          );
        })}
      </div>
    ));
  }

  get PriceRange() {
    const { productFamily } = this.props;

    if (productFamily.price_min === productFamily.price_max) {
      return productFamily.price_min;
    }
    return `${productFamily.price_min} – ${productFamily.price_max}`;
  }

  render() {
    const { products, productFamily, brand, waterbody, className } = this.props;
    const expandedClass = this.state.expanded ? styles.family__expanded : null;
    const height = this.state.expanded ? 'auto' : 0;
    const duration = products.length * 75;
    let durationNormalized = Math.max(duration, 300);
    durationNormalized = Math.min(durationNormalized, 1000);
    const isNewRelease = productFamily.tags.indexOf('new_releases') > -1;
    const hasCompareAt = products.some((product) => product.on_sale);
    const highestCompareAt = products
      .map((p) => Number(p.compare_at_price))
      .filter(Boolean)
      .sort()[0];

    return (
      <div className={classNames(styles.family, className, expandedClass)}>
        <div className={styles.overview} ref={this.divRef}>
          <div className={styles.img}>
            <Link
              to={`${RoutePaths.PRODUCTS}/${productFamily.handle}`}
              onClick={() => {
                WebAnalytics.productClick({
                  productOrProductFamily: productFamily,
                  show_modal: false,
                  position: null,
                });
              }}
            >
              <img src={setShopifyImgWidth(productFamily.img_url, 300)} alt={productFamily.title} />
            </Link>
          </div>
          <div className={styles.options}>
            <div>
              <Link
                to={`${RoutePaths.PRODUCTS}/${productFamily.handle}`}
                className={styles.title}
                onClick={() => {
                  WebAnalytics.productClick({
                    productOrProductFamily: productFamily,
                    show_modal: false,
                    position: null,
                  });
                }}
              >
                {productFamily.title}
                {isNewRelease && <span className={styles.newReleaseTag}>NEW RELEASE</span>}
              </Link>
            </div>
            {this.ColorOption}
            {this.NonColorOptions}
          </div>
          <div className={styles.details}>
            {brand && brand.image && (
              <img
                src={getImgixPath(brand.image, { w: 300 })}
                alt={brand.display_name}
                className={styles.brandImg}
              />
            )}
            <p className={styles.price}>
              <span
                className={classNames({
                  [styles.price__sale]: hasCompareAt,
                })}
              >
                ${this.PriceRange}
              </span>
            </p>
            {hasCompareAt && <s className={styles.priceCompare}>{toDollars(highestCompareAt)}</s>}
            <Link
              to={`${RoutePaths.PRODUCTS}/${productFamily.handle}`}
              className={`ant-btn ant-btn-block ${styles.detailsButton}`}
              onClick={() => {
                WebAnalytics.productClick({
                  productOrProductFamily: productFamily,
                  show_modal: false,
                  position: null,
                });
              }}
            >
              Product Details
            </Link>
            {this.ListButton}
          </div>
        </div>
        <AnimateHeight duration={durationNormalized} height={height} easing="ease-out">
          <ProductDetailGrid
            fishingReports={[]}
            productFamily={productFamily}
            waterbody={waterbody}
            shopifyProduct={null}
          />
        </AnimateHeight>
        {this.ExpandButton}
      </div>
    );
  }
}
